import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import config from '../../config';
import { useAppContext } from '../AppContext';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Stack,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const extractUsername = (item) => {
  if (!item) return 'Desconegut';
  if (typeof item.usuari === 'string') return item.usuari;
  if (item.usuari && typeof item.usuari === 'object' && item.usuari.usuari) {
    return item.usuari.usuari;
  }
  if (typeof item.usuariNom === 'string') return item.usuariNom;
  return 'Sense usuari';
};

const extractUserapp = (item) => {
  if (!item) return '';
  if (typeof item.userapp === 'string') return item.userapp;
  if (item.userapp && typeof item.userapp === 'object' && item.userapp.userapp) {
    return item.userapp.userapp;
  }
  return '';
};

const extractErrorMessage = (err, fallback) => {
  if (err.response?.status === 403) {
    return "Accés denegat (403): Cal el rol ROLE_ADMIN. Fes Logout i Login amb Google per actualitzar el token.";
  }
  return (
    err.response?.data?.detail ||
    err.response?.data?.message ||
    (typeof err.response?.data === 'string' ? err.response.data : null) ||
    fallback
  );
};

const UsuarisComptesAdmin = ({ showNotification, onReloadAll }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { ctxUser } = useAppContext();
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';

  const [assignments, setAssignments] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('ALL');
  const [filterApp, setFilterApp] = useState('ALL');
  const [onlyActive, setOnlyActive] = useState(false);

  // Modal Crear / Editar
  const [openModal, setOpenModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    usuari: '',
    userapp: '',
    ordre: 1,
    actiu: true
  });
  const [saving, setSaving] = useState(false);


  // Carregar assignacions
  const fetchAssignments = useCallback(async () => {
    if (!jwtToken) return;
    setLoading(true);
    try {
      // Obtenir usuaris i assignacions en paral·lel o utilitzar els existents
      const [resAssignments, resUsers] = await Promise.all([
        axios.get(`${config.apiBaseUrl}/v1/usuariscomptes/list`, {
          headers: { Authorization: jwtToken }
        }),
        axios.get(`${config.apiBaseUrl}/v1/usuari/list`, {
          headers: { Authorization: jwtToken }
        })
      ]);

      const rawUsers = Array.isArray(resUsers.data) ? resUsers.data : [];
      setUsersList(rawUsers);

      // Mapa de compte.id -> nom d'usuari
      const userByCompteId = {};
      rawUsers.forEach((u) => {
        (u.comptes || []).forEach((c) => {
          if (c.id) {
            userByCompteId[c.id] = u.usuari;
          }
        });
      });

      const rawData = Array.isArray(resAssignments.data) ? resAssignments.data : [];
      let normalized = rawData.map((item) => {
        const directUser = extractUsername(item);
        const resolvedUser =
          directUser && directUser !== 'Sense usuari' && directUser !== 'Desconegut'
            ? directUser
            : userByCompteId[item.id] || 'Sense usuari';

        return {
          ...item,
          usuari: resolvedUser,
          userapp: extractUserapp(item),
          ordre: typeof item.ordre === 'number' ? item.ordre : Number(item.ordre) || 1,
          actiu: item.actiu ?? true
        };
      });

      // Si l'endpoint de usuariscomptes estigués buit, extreure de l'arbre d'usuaris
      if (normalized.length === 0 && rawUsers.length > 0) {
        rawUsers.forEach((u) => {
          (u.comptes || []).forEach((c) => {
            normalized.push({
              id: c.id,
              usuari: u.usuari,
              userapp: c.userapp,
              ordre: typeof c.ordre === 'number' ? c.ordre : Number(c.ordre) || 1,
              actiu: c.actiu ?? true
            });
          });
        });
      }

      const sorted = [...normalized].sort((a, b) => {
        const uA = a.usuari || '';
        const uB = b.usuari || '';
        if (uA === uB) {
          return (a.ordre || 0) - (b.ordre || 0);
        }
        return uA.localeCompare(uB);
      });
      setAssignments(sorted);
    } catch (err) {
      console.error('Error al carregar usuaris_comptes:', err);
      const msg = extractErrorMessage(err, 'Error al carregar assignacions de comptes');
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  // Llista d'usuaris únics i comptes únics presents
  const uniqueUsers = useMemo(() => {
    const set = new Set();
    assignments.forEach((a) => {
      if (a.usuari && a.usuari !== 'Sense usuari' && a.usuari !== 'Desconegut') {
        set.add(a.usuari);
      }
    });
    usersList.forEach((u) => {
      if (u.usuari) set.add(u.usuari);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [assignments, usersList]);

  const uniqueApps = useMemo(() => {
    const set = new Set();
    assignments.forEach((a) => {
      if (a.userapp) set.add(a.userapp);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [assignments]);

  // Filtrar assignacions
  const filteredAssignments = useMemo(() => {
    return assignments.filter((a) => {
      const q = (searchTerm || '').toLowerCase();
      const uStr = (a.usuari || '').toLowerCase();
      const appStr = (a.userapp || '').toLowerCase();
      const matchesSearch = uStr.includes(q) || appStr.includes(q);
      const matchesUser = filterUser === 'ALL' || a.usuari === filterUser;
      const matchesApp = filterApp === 'ALL' || a.userapp === filterApp;
      const matchesActive = !onlyActive || Boolean(a.actiu);
      return matchesSearch && matchesUser && matchesApp && matchesActive;
    });
  }, [assignments, searchTerm, filterUser, filterApp, onlyActive]);

  // Obrir Creació
  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({
      usuari: usersList[0]?.usuari || uniqueUsers[0] || '',
      userapp: uniqueApps[0] || '',
      ordre: 1,
      actiu: true
    });
    setOpenModal(true);
  };

  // Obrir Edició
  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      usuari: item.usuari,
      userapp: item.userapp,
      ordre: item.ordre || 1,
      actiu: item.actiu ?? true
    });
    setOpenModal(true);
  };

  // Desar
  const handleSave = async () => {
    if (!formData.usuari.trim()) {
      showNotification('Selecciona o indica un usuari', 'warning');
      return;
    }
    if (!formData.userapp.trim()) {
      showNotification('Indica el compte (userapp)', 'warning');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        // PUT /api/v1/usuariscomptes/{id}
        await axios.put(
          `${config.apiBaseUrl}/v1/usuariscomptes/${editingItem.id}`,
          {
            id: editingItem.id,
            usuari: formData.usuari,
            userapp: formData.userapp.trim().toUpperCase(),
            ordre: Number(formData.ordre) || 1,
            actiu: formData.actiu
          },
          { headers: { Authorization: jwtToken } }
        );
        showNotification('Assignació actualitzada correctament', 'success');
      } else {
        // POST /api/v1/usuariscomptes/
        await axios.post(
          `${config.apiBaseUrl}/v1/usuariscomptes/`,
          {
            usuari: formData.usuari.trim().toLowerCase(),
            userapp: formData.userapp.trim().toUpperCase(),
            ordre: Number(formData.ordre) || 1,
            actiu: formData.actiu
          },
          { headers: { Authorization: jwtToken } }
        );
        showNotification('Nova assignació creada correctament', 'success');
      }
      setOpenModal(false);
      fetchAssignments();
      if (onReloadAll) onReloadAll();
    } catch (err) {
      console.error('Error al desar assignació:', err);
      const msg = extractErrorMessage(err, 'Error en desar l’assignació');
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Toggle Actiu
  const handleToggle = async (item) => {
    try {
      await axios.patch(
        `${config.apiBaseUrl}/v1/usuariscomptes/${item.id}/toggle-actiu`,
        {},
        { headers: { Authorization: jwtToken } }
      );
      showNotification(`Estat d’accés al compte ${item.userapp} actualitzat`, 'success');
      fetchAssignments();
      if (onReloadAll) onReloadAll();
    } catch (err) {
      console.error('Error al commutar estat:', err);
      const msg = extractErrorMessage(err, 'Error al canviar l’estat');
      showNotification(msg, 'error');
    }
  };

  // Eliminar
  const handleDelete = async (item) => {
    if (!window.confirm(`Vols suprimir l’assignació del compte ${item.userapp} per a l’usuari ${item.usuari}?`)) {
      return;
    }
    try {
      await axios.delete(`${config.apiBaseUrl}/v1/usuariscomptes/${item.id}`, {
        headers: { Authorization: jwtToken }
      });
      showNotification(`Assignació suprimida correctament`, 'success');
      fetchAssignments();
      if (onReloadAll) onReloadAll();
    } catch (err) {
      console.error('Error al suprimir assignació:', err);
      const msg = extractErrorMessage(err, 'Error al suprimir l’assignació');
      showNotification(msg, 'error');
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Filtres i Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Cercar usuari o compte..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Filtrar Usuari</InputLabel>
              <Select
                value={filterUser}
                label="Filtrar Usuari"
                onChange={(e) => setFilterUser(e.target.value)}
              >
                <MenuItem value="ALL">Tots els usuaris</MenuItem>
                {uniqueUsers.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Filtrar Compte</InputLabel>
              <Select
                value={filterApp}
                label="Filtrar Compte"
                onChange={(e) => setFilterApp(e.target.value)}
              >
                <MenuItem value="ALL">Tots els comptes</MenuItem>
                {uniqueApps.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={onlyActive}
                  onChange={(e) => setOnlyActive(e.target.checked)}
                />
              }
              label={<Typography variant="caption">Només actius</Typography>}
            />
          </Grid>
          <Grid item xs={6} sm={12} md={2} sx={{ textAlign: { xs: 'right', md: 'right' } }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              fullWidth={isMobile}
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
            >
              Nova Assignació
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Estat de càrrega */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredAssignments.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No s’ha trobat cap assignació amb els filtres seleccionats.
        </Alert>
      ) : isMobile ? (
        /* VISTA MÒBIL: LLISTA DE TARGETES */
        <Stack spacing={1.5}>
          {filteredAssignments.map((a) => (
            <Card key={a.id} variant="outlined" sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {a.usuari}
                  </Typography>
                  <Chip
                    label={a.actiu ? 'Actiu' : 'Inactiu'}
                    color={a.actiu ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2">
                    <strong>Compte:</strong> {a.userapp}
                  </Typography>
                  <Chip
                    label={`Ordre: ${a.ordre}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                </Box>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(a.actiu)}
                      onChange={() => handleToggle(a)}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      {a.actiu ? 'Permès' : 'Blocat'}
                    </Typography>
                  }
                />
                <Box>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenEdit(a)}
                    sx={{ textTransform: 'none', mr: 0.5 }}
                  >
                    Editar
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(a)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        /* VISTA ESCRIPTORI: TAULA COMPLETA */
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Usuari</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Compte (UserApp)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ordre de Preferència</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estat Permís</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Accions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAssignments.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{a.usuari}</TableCell>
                  <TableCell>
                    <Chip label={a.userapp} color="primary" variant="outlined" size="small" />
                  </TableCell>
                  <TableCell align="center">{a.ordre}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={a.actiu ? 'Actiu' : 'Inactiu'}
                      color={a.actiu ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar ordre i estat">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(a)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={a.actiu ? 'Desactivar permís' : 'Activar permís'}>
                      <IconButton
                        size="small"
                        color={a.actiu ? 'default' : 'success'}
                        onClick={() => handleToggle(a)}
                      >
                        <PowerSettingsNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Suprimir assignació">
                      <IconButton size="small" color="error" onClick={() => handleDelete(a)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* DIÀLEG: CREAR / EDITAR ASSIGNACIÓ */}
      <Dialog
        open={openModal}
        onClose={() => !saving && setOpenModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceIcon color="primary" />
          {editingItem ? 'Editar Assignació de Compte' : 'Nova Assignació de Compte'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {editingItem ? (
              <TextField
                label="Usuari"
                fullWidth
                disabled
                value={formData.usuari}
              />
            ) : (
              <FormControl fullWidth required>
                <InputLabel>Usuari</InputLabel>
                <Select
                  value={formData.usuari}
                  label="Usuari"
                  onChange={(e) => setFormData({ ...formData, usuari: e.target.value })}
                >
                  {usersList.map((u) => (
                    <MenuItem key={u.usuari} value={u.usuari}>
                      {u.usuari} ({u.email})
                    </MenuItem>
                  ))}
                  {/* Si l'usuari no és a la llista d'usuaris però existeix com a opció */}
                  {formData.usuari && !usersList.some((u) => u.usuari === formData.usuari) && (
                    <MenuItem value={formData.usuari}>{formData.usuari}</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Codi de Compte (UserApp)"
              fullWidth
              required
              disabled={Boolean(editingItem)}
              value={formData.userapp}
              onChange={(e) => setFormData({ ...formData, userapp: e.target.value.toUpperCase() })}
              placeholder="Ex: CFG, ESTALVI..."
              helperText="Identificador del compte"
            />

            <TextField
              label="Ordre de Preferència"
              type="number"
              fullWidth
              required
              value={formData.ordre}
              onChange={(e) => setFormData({ ...formData, ordre: e.target.value })}
              helperText="Ordre en què apareixerà als menús i desplegables"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.actiu}
                  onChange={(e) => setFormData({ ...formData, actiu: e.target.checked })}
                  color="success"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Accés permès (Actiu)
                </Typography>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenModal(false)} disabled={saving} color="inherit">
            Cancel·lar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsuarisComptesAdmin;
